import { useQueryClient } from '@tanstack/react-query'
import { createContext, type PropsWithChildren, useContext } from 'react'

import { getGetEmployersEmployersIdQueryKey, useGetEmployersEmployersId, usePutEmployersEmployersIdProfile } from 'utils/web_api'
import { type EmployersEmployer } from 'utils/web_api.schemas'

export type EmployerProviderProps = {
  hasFinalizedPricing: boolean
  employerIsEstimatedToBeALE: boolean
  employerId: string
}

type EmployerContextType = EmployerProviderProps & {
  employerIsALE?: boolean
  updateEmployersALEStatus: (isALE: boolean) => Promise<void>
  updateEmployerIsLoading: boolean
}

const EmployerContext = createContext<EmployerContextType>({
  hasFinalizedPricing: false,
  employerIsEstimatedToBeALE: false,
  employerId: '',
  updateEmployersALEStatus: () => Promise.resolve(),
  updateEmployerIsLoading: false,
})

export function useEmployer () {
  return useContext(EmployerContext)
}

export function useHasFinalizedPricing () {
  return useEmployer().hasFinalizedPricing
}

export default function EmployerProvider ({
  hasFinalizedPricing,
  employerIsEstimatedToBeALE,
  employerId,
  children,
}: PropsWithChildren<EmployerProviderProps>) {
  const updateEmployerProfileMutation = usePutEmployersEmployersIdProfile()
  const queryClient = useQueryClient()

  const { data: employer } = useGetEmployersEmployersId(employerId)

  const updateEmployersALEStatus = async (isALE: boolean) => {
    await updateEmployerProfileMutation.mutateAsync({
      id: employerId,
      data: {
        considered_applicable_large_employer: isALE,
      },
    })
    queryClient.setQueryData(getGetEmployersEmployersIdQueryKey(employerId), (previousEmployer: EmployersEmployer) => {
      return {
        ...previousEmployer,
        considered_applicable_large_employer: isALE,
      }
    })
  }

  return (
    <EmployerContext.Provider
      value={{
        hasFinalizedPricing,
        employerIsEstimatedToBeALE,
        employerIsALE: employer?.considered_applicable_large_employer ?? undefined,
        updateEmployersALEStatus,
        updateEmployerIsLoading: updateEmployerProfileMutation.isPending,
        employerId,
      }}
    >
      {children}
    </EmployerContext.Provider>
  )
}
